/**
 * A horizontal scrollbar pinned to the bottom of the list.
 *
 * WHY THIS EXISTS
 * A scrollbar belongs to its scroll box and the browser draws it at that box's
 * bottom edge. On a list of ten rows the table's bottom edge is below the fold,
 * so a mouse user had to scroll to the end of the page, drag sideways, then
 * scroll back up to read the row they were on. Trackpad users swipe sideways and
 * never meet the problem, which is why it went unnoticed for so long.
 *
 * There is no CSS that moves a scrollbar away from its box, so the bar the user
 * drags here is a PROXY: an empty strip, stuck to the bottom of the viewport,
 * holding a rail as wide as the table. Scrolling either one drives the other.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * An earlier attempt solved this by rebuilding each list screen into a
 * viewport-sized box, so the table scrolled inside itself. That put the bar on
 * screen but changed how the whole page behaved -- the page stopped scrolling
 * and the header had to be pinned to compensate -- and was rejected and
 * reverted. Nothing here touches page height, vertical scrolling, the header, or
 * any existing layout rule. The only new thing is the strip.
 *
 * WHERE THE PROXY LIVES
 * As a sibling of the table card, appended to the `.o-list` root -- NOT inside
 * the card. `.o-list__card` sets `overflow: hidden` for its rounded corners
 * (app_css/list-screen.scss), and an ancestor whose overflow is anything but
 * `visible` confines a `position: sticky` child to that ancestor. Inside the
 * card the strip would pin to the card's own bottom, which is exactly the
 * off-screen place we are trying to escape. `collections.scss` documents the
 * same trap from the last time it caught someone here.
 *
 * WHY IT ATTACHES ITSELF
 * The reverted attempt named the list screens' root classes by hand and silently
 * missed four of them; the gap was found in use, not in review. This finds its
 * own targets, so a screen added later is covered without anyone remembering to
 * add it to a list.
 */

const PROXY_CLASS = "o-xbar";
const RAIL_CLASS = "o-xbar__rail";
/* On the pinned pager while the strip sits on it, so the CSS can merge the two
   into one bottom panel. On the pager and not the list root: the root holds the
   whole table, and a class change there restyled every element on the page
   (~1,300 with ten rows) each time the strip came or went. */
const FOOT_CLASS = "o-foot--xbar";

/* One entry per wrapper currently being tracked, so teardown can be exact. */
let tracked = [];

function isOverflowing(wrapper) {
  return wrapper.scrollWidth - wrapper.clientWidth > 1;
}

/* The table's OWN bar is already usable when the wrapper's bottom edge sits
   inside the viewport -- short tables, mostly. Showing a proxy as well would put
   two horizontal bars a few pixels apart, both driving the same table.
   `footH` is subtracted because the pager is pinned over the bottom of the
   screen: a bar hiding BEHIND it is not reachable, so it does not count as
   visible and the proxy is still needed. */
function ownBarVisible(wrapper, footH) {
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return wrapper.getBoundingClientRect().bottom <= vh - footH;
}

/* The pager is pinned to bottom:0 by CSS (see `.o-list ...:has(.v-pagination)`
   in app_css/list-screen.scss). The strip has to sit ON TOP of it rather than
   underneath, so it is offset by however tall the pager turns out to be —
   measured, not assumed, because the count line wraps on a narrow window and
   Collections' pager carries no footer chrome at all. Published as a custom
   property on the root so the offset stays in CSS where the rest of the
   positioning lives. */
function syncFootOffset(root, entry) {
  // `.o-pager` is ListPager's root and is present whether or not the list has
  // page numbers; `.v-pagination` alone missed every single-page list, leaving
  // the strip to sit behind the footer instead of on top of it.
  const pager = root.querySelector(".o-pager") || root.querySelector(".v-pagination");
  // Never `.o-pager`'s parent: without a footer that is the table's own
  // wrapper, and its full height would lift the strip off the screen.
  const foot = pager
    ? pager.closest('[class*="__foot"]') ||
      (pager.classList.contains("o-pager") ? pager : pager.parentElement)
    : null;
  // The element the pinned-pager rule draws its border on: the footer, or the
  // div holding a hand-rolled `<nav><ul class="v-pagination">`.
  const edge = pager ? pager.closest('[class*="__foot"]') || pager.closest("div") : null;
  if (entry.edge && entry.edge !== edge) entry.edge.classList.remove(FOOT_CLASS);
  entry.edge = edge;
  const h = foot ? Math.round(foot.getBoundingClientRect().height) : 0;
  // Only on a change: the property is set on the list root and inherited by
  // every element under it.
  if (root.style.getPropertyValue("--o-foot-h") !== h + "px") {
    root.style.setProperty("--o-foot-h", h + "px");
  }
  return h;
}

function setShown(entry, shown) {
  entry.shown = shown;
  if (entry.edge) entry.edge.classList.toggle(FOOT_CLASS, shown);
}

function measure(entry) {
  const { wrapper, proxy, rail } = entry;

  if (!wrapper.isConnected) {
    return teardownOne(entry);
  }

  const root = proxy.parentElement;
  const footH = root ? syncFootOffset(root, entry) : 0;
  entry.footH = footH;
  entry.overflows = isOverflowing(wrapper);

  if (!entry.overflows) {
    proxy.style.display = "none";
    setShown(entry, false);
    return;
  }

  /* Laid out for as long as the table overflows, and only made visible or
     invisible as the table's own bar comes in and out of reach. Toggling
     `display` on every reach change grew and shrank the page under the
     scroll -- by 10-12px even with the strip's margin pulled back, through
     margin collapsing -- so near the bottom the scroll position snapped back.
     `visibility` never moves anything, and a hidden strip takes no clicks. */
  proxy.style.display = "block";
  const box = wrapper.getBoundingClientRect();
  const parentBox = proxy.parentElement.getBoundingClientRect();
  /* Positioned against the root rather than inheriting its full width: the card
     can be inset from the root (padding, or a narrower card on some screens) and
     a mismatched strip puts the thumb out of step with the columns it moves. */
  proxy.style.width = box.width + "px";
  proxy.style.marginLeft = box.left - parentBox.left + "px";
  rail.style.width = wrapper.scrollWidth + "px";

  const shown = !ownBarVisible(wrapper, footH);
  proxy.style.visibility = shown ? "" : "hidden";
  // Lets the CSS merge the strip and the pager into one bottom panel.
  setShown(entry, shown);
  /* Adopt the table's current position, so the thumb starts where the reader
     left the columns rather than snapping them back to the left edge. */
  if (shown) proxy.scrollLeft = wrapper.scrollLeft;
}

function attach(wrapper, root) {
  const proxy = document.createElement("div");
  proxy.className = PROXY_CLASS;
  proxy.setAttribute("aria-hidden", "true");
  const rail = document.createElement("div");
  rail.className = RAIL_CLASS;
  proxy.appendChild(rail);
  root.appendChild(proxy);

  /* Echo guard. Assigning scrollLeft fires `scroll` on the target, which would
     assign back to the source -- the two bars fight and the thumb stutters.
     Each side remembers the position it last WROTE to the other and ignores the
     scroll event that lands on exactly that position: that event is its own
     echo, not the user.
     It used to be a flag cleared on the next animation frame, but the echo
     often arrives a frame later, after the flag was gone. It then wrote the
     echo's stale position back onto the side the user was moving. That cut a
     smooth wheel glide short and snapped it (measured: 0 -> 424 -> 900 in one
     frame) and pulled the table back a step mid-swipe, which read as
     flickering. */
  let wroteWrapper = null;
  let wroteProxy = null;
  const fromProxy = () => {
    const x = proxy.scrollLeft;
    const echo = wroteProxy !== null && Math.abs(x - wroteProxy) < 1;
    wroteProxy = null;
    if (echo || Math.abs(x - wrapper.scrollLeft) < 1) return;
    wroteWrapper = x;
    wrapper.scrollLeft = x;
  };
  const fromWrapper = () => {
    const x = wrapper.scrollLeft;
    const echo = wroteWrapper !== null && Math.abs(x - wroteWrapper) < 1;
    wroteWrapper = null;
    if (echo || Math.abs(x - proxy.scrollLeft) < 1) return;
    wroteProxy = x;
    proxy.scrollLeft = x;
  };

  proxy.addEventListener("scroll", fromProxy, { passive: true });
  wrapper.addEventListener("scroll", fromWrapper, { passive: true });

  const entry = {
    wrapper,
    proxy,
    rail,
    fromProxy,
    fromWrapper,
    ro: null,
    edge: null,
    footH: 0,
    overflows: false,
    shown: false,
  };

  /* `scrollWidth` moves with the column set, the sidebar, the window and the
     rows themselves -- a tab switch on LMS swaps twelve columns for ten. Observe
     the wrapper and the table inside it rather than guessing when to re-measure. */
  if (typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver(() => measure(entry));
    ro.observe(wrapper);
    const table = wrapper.querySelector("table");
    if (table) ro.observe(table);
    entry.ro = ro;
  }

  tracked.push(entry);
  measure(entry);
  return entry;
}

function teardownOne(entry) {
  const i = tracked.indexOf(entry);
  if (i !== -1) tracked.splice(i, 1);
  entry.proxy.removeEventListener("scroll", entry.fromProxy);
  entry.wrapper.removeEventListener("scroll", entry.fromWrapper);
  if (entry.ro) entry.ro.disconnect();
  if (entry.edge) entry.edge.classList.remove(FOOT_CLASS);
  if (entry.proxy.parentElement) entry.proxy.remove();
}

export function teardown() {
  tracked.slice().forEach(teardownOne);
}

/* Idempotent: safe to call on every navigation and whenever rows land. */
export function refresh() {
  const wrappers = Array.from(
    document.querySelectorAll(".o-list .v-data-table__wrapper")
  );

  /* Drop anything whose table has gone (route change, tab swap). */
  tracked
    .filter((e) => !wrappers.includes(e.wrapper) || !e.wrapper.isConnected)
    .forEach(teardownOne);

  wrappers.forEach((wrapper) => {
    const root = wrapper.closest(".o-list");
    if (!root) return;
    const existing = tracked.find((e) => e.wrapper === wrapper);
    if (existing) measure(existing);
    else attach(wrapper, root);
  });
}

function remeasureAll() {
  tracked.forEach(measure);
}

/* What page scrolling can change is only whether the table's own bar is in
   reach, so that is all a scroll checks: one rect read per table, once a frame,
   and the full measure only when the answer flips. Measuring on every scroll
   event wrote styles and then read layout, forcing the browser to restyle the
   page inside the scroll itself (up to 43ms an event on Collections). */
let reachFrame = 0;

function checkReach() {
  reachFrame = 0;
  tracked.forEach((entry) => {
    if (!entry.wrapper.isConnected) return; // refresh() tears it down
    const shown = entry.overflows && !ownBarVisible(entry.wrapper, entry.footH);
    if (shown !== entry.shown) measure(entry);
  });
}

function onScroll() {
  if (!reachFrame) reachFrame = requestAnimationFrame(checkReach);
}

export default {
  install(Vue, { router } = {}) {
    if (typeof window === "undefined") return;

    /* Rows arrive after navigation, and Vuetify rebuilds the table when a tab
       changes without a route change, so a single post-navigation pass is not
       enough. A short settle sequence covers the common cases; the observer
       below covers the rest. */
    const settle = () => {
      refresh();
      setTimeout(refresh, 300);
      setTimeout(refresh, 1200);
    };

    if (router) {
      router.afterEach(() => {
        teardown();
        settle();
      });
    }

    window.addEventListener("resize", remeasureAll, { passive: true });
    /* The proxy's visibility depends on where the wrapper's bottom edge is, and
       that changes as the page scrolls -- scrolling to the end of a short list
       should hand the job back to the table's own bar. */
    window.addEventListener("scroll", onScroll, { passive: true });

    if (typeof MutationObserver !== "undefined") {
      let queued = false;
      const mo = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          refresh();
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    Vue.nextTick(settle);
  },
};
